import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Card from '../common/Card';
import Button from '../common/Button';
import { postsAPI, uploadAPI, aiAPI } from '../../services/api';
import { handleApiError, getImageUrl, showToast } from '../../utils/helpers';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Custom styles for Quill editor in RTL mode
const quillStyles = `
  .ql-editor {
    min-height: 200px;
  }
  .ql-container {
    font-size: 14px;
  }
`;

const ProductArticleDetailsCard = ({ post, onUpdate }) => {
  const { isAdmin, isDesigner, isWriter } = useAuth();
  const { isRTL } = useLanguage();
  const canEdit = isAdmin || isDesigner || isWriter;
  const isProduct = post?.content_type === 'product';
  const isArticle = post?.content_type === 'blog';

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingAIContent, setGeneratingAIContent] = useState(false);
  const [generatingAISEO, setGeneratingAISEO] = useState(false);

  const [formData, setFormData] = useState({
    rich_description: '',
    meta_title: '',
    meta_description: '',
    seo_keywords: [],
    product_details: {},
    article_details: {},
  });

  // Track last post id to prevent unnecessary updates
  const lastPostIdRef = React.useRef(null);
  const isInitializedRef = React.useRef(false);

  // Initialize formData only when post.id changes - NEVER update while editing
  useEffect(() => {
    // Only update if:
    // 1. Post exists
    // 2. Not currently editing (to prevent overwriting user input)
    // 3. Post ID changed (new post loaded)
    if (post?.id && !editing && post.id !== lastPostIdRef.current) {
      setFormData({
        rich_description: post.rich_description || '',
        meta_title: post.meta_title || '',
        meta_description: post.meta_description || '',
        seo_keywords: post.seo_keywords || [],
        product_details: post.product_details || {},
        article_details: post.article_details || {},
      });
      lastPostIdRef.current = post.id;
      isInitializedRef.current = true;
    }
  }, [post?.id, editing]); // Only depend on post.id and editing state

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  };

  const handleProductDetailChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      product_details: {
        ...prev.product_details,
        [field]: value,
      },
    }));
  };

  const handleArticleDetailChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      article_details: {
        ...prev.article_details,
        [field]: value,
      },
    }));
  };

  const handleKeywordsChange = (e) => {
    const value = e.target.value;
    const keywords = value.split(',').map(k => k.trim()).filter(k => k.length > 0);
    handleChange('seo_keywords', keywords);
  };

  const handleGenerateAIDescription = async () => {
    if (!post?.id) return;

    try {
      setGeneratingAIContent(true);
      setError(null);

      let response;
      if (isProduct) {
        response = await aiAPI.generateProductDescription(post.id, {
          language: isRTL ? 'arabic' : 'english',
          style: 'marketing',
        });
        const generatedDescription = response.data.description;
        handleChange('rich_description', generatedDescription);
      } else if (isArticle) {
        response = await aiAPI.generateArticleContent(post.id, {
          language: isRTL ? 'arabic' : 'english',
          length: 'medium',
          style: 'blog',
        });
        const generatedContent = response.data.content;
        handleChange('rich_description', generatedContent);
      }

      // Open edit mode to show generated content
      if (!editing) {
        setEditing(true);
      }

      showToast.success(isRTL 
        ? 'تم توليد المحتوى بنجاح! يمكنك تعديله قبل الحفظ.'
        : 'Content generated successfully! You can edit it before saving.'
      );
    } catch (err) {
      console.error('AI generation error:', err);
      const errorMessage = handleApiError(err);
      if (errorMessage.includes('not configured')) {
        setError(isRTL 
          ? 'خدمة AI غير مفعلة. يرجى إضافة GEMINI_API_KEY في إعدادات السيرفر.'
          : 'AI service not configured. Please add GEMINI_API_KEY to server environment variables.'
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setGeneratingAIContent(false);
    }
  };

  const handleGenerateAISEO = async () => {
    if (!post?.id) return;

    try {
      setGeneratingAISEO(true);
      setError(null);

      const response = await aiAPI.generateSEO(post.id, {
        language: isRTL ? 'arabic' : 'english',
      });

      const seoData = response.data;
      
      if (seoData.meta_title) {
        handleChange('meta_title', seoData.meta_title);
      }
      if (seoData.meta_description) {
        handleChange('meta_description', seoData.meta_description);
      }
      if (seoData.seo_keywords && Array.isArray(seoData.seo_keywords)) {
        handleChange('seo_keywords', seoData.seo_keywords);
      }

      // Open edit mode to show generated SEO
      if (!editing) {
        setEditing(true);
      }

      showToast.success(isRTL 
        ? 'تم توليد بيانات SEO بنجاح! يمكنك تعديلها قبل الحفظ.'
        : 'SEO metadata generated successfully! You can edit it before saving.'
      );
    } catch (err) {
      console.error('AI SEO generation error:', err);
      const errorMessage = handleApiError(err);
      if (errorMessage.includes('not configured')) {
        setError(isRTL 
          ? 'خدمة AI غير مفعلة. يرجى إضافة GEMINI_API_KEY في إعدادات السيرفر.'
          : 'AI service not configured. Please add GEMINI_API_KEY to server environment variables.'
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setGeneratingAISEO(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Clean up data before sending
      const submitData = {
        rich_description: formData.rich_description && formData.rich_description.trim() 
          ? formData.rich_description.trim() 
          : null,
        meta_title: formData.meta_title && formData.meta_title.trim() 
          ? formData.meta_title.trim() 
          : null,
        meta_description: formData.meta_description && formData.meta_description.trim() 
          ? formData.meta_description.trim() 
          : null,
        seo_keywords: Array.isArray(formData.seo_keywords) ? formData.seo_keywords : [],
        product_details: formData.product_details && Object.keys(formData.product_details).length > 0 
          ? formData.product_details 
          : null,
        article_details: formData.article_details && Object.keys(formData.article_details).length > 0 
          ? formData.article_details 
          : null,
      };

      await postsAPI.update(post.id, submitData);

      setEditing(false);
      
      // Reset to allow formData to update with new post data after save
      isInitializedRef.current = false;
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const quillRef = useRef(null);

  // Handle image upload for Quill editor - use useCallback to prevent recreation
  const handleImageUpload = useCallback(async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast.error(isRTL ? 'حجم الصورة كبير جداً (الحد الأقصى 5MB)' : 'Image size is too large (max 5MB)');
        return;
      }

      try {
        setLoading(true);
        const response = await uploadAPI.uploadImage(file);
        const imageUrl = getImageUrl(response.data.url || response.data.imageUrl);

        // Insert image into Quill editor - add safety check
        const quill = quillRef.current?.getEditor();
        if (quill) {
          try {
            const range = quill.getSelection(true);
            if (range) {
              quill.insertEmbed(range.index, 'image', imageUrl);
              quill.setSelection(range.index + 1);
            }
          } catch (err) {
            // If selection fails, just insert at the end
            const length = quill.getLength();
            quill.insertEmbed(length - 1, 'image', imageUrl);
          }
        }
      } catch (err) {
        showToast.error(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };
  }, [isRTL]);

  // Memoize modules to prevent recreation on every render
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'color': [] }, { 'background': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: handleImageUpload,
      },
    },
  }), [handleImageUpload]);

  // Memoize formats
  const formats = useMemo(() => [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'link',
    'image'
  ], []);

  if (!isProduct && !isArticle) {
    return null;
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {isRTL 
            ? (isProduct ? 'تفاصيل المنتج' : 'تفاصيل المقالة')
            : (isProduct ? 'Product Details' : 'Article Details')
          }
        </h2>
        {canEdit && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (editing) {
                setFormData({
                  rich_description: post.rich_description || '',
                  meta_title: post.meta_title || '',
                  meta_description: post.meta_description || '',
                  seo_keywords: post.seo_keywords || [],
                  product_details: post.product_details || {},
                  article_details: post.article_details || {},
                });
                setError(null);
              }
              setEditing(!editing);
            }}
          >
            {editing ? (isRTL ? 'إلغاء' : 'Cancel') : '✏️ Edit'}
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Rich Description (Rich Text Editor) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              {isRTL 
                ? (isProduct ? 'الوصف المفصل للمنتج' : 'محتوى المقالة')
                : (isProduct ? 'Product Description' : 'Article Content')
              }
            </label>
            {canEdit && !editing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleGenerateAIDescription}
                loading={generatingAIContent}
                disabled={generatingAIContent}
              >
                {isRTL ? '✨ توليد بـ AI' : '✨ Generate with AI'}
              </Button>
            )}
          </div>
          {editing ? (
            <div className="quill-wrapper" style={{ minHeight: '250px' }}>
              <ReactQuill
                key={`quill-editor-${post?.id}`}
                ref={quillRef}
                theme="snow"
                value={formData.rich_description || ''}
                onChange={(value) => {
                  handleChange('rich_description', value);
                }}
                modules={modules}
                formats={formats}
                className="bg-white"
                style={{ minHeight: '200px' }}
                placeholder={isRTL ? 'ابدأ الكتابة...' : 'Start typing...'}
              />
            </div>
          ) : (
            <div 
              className="prose max-w-none p-4 border border-gray-200 rounded-lg bg-gray-50 min-h-[100px]"
              dangerouslySetInnerHTML={{ __html: formData.rich_description || (isRTL ? '(لا يوجد وصف)' : '(No description)') }}
            />
          )}
        </div>

        {/* Product-specific fields */}
        {isProduct && editing && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'السعر' : 'Price'}
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.product_details.price || ''}
                onChange={(e) => handleProductDetailChange('price', parseFloat(e.target.value) || 0)}
                className="input w-full"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'العملة' : 'Currency'}
              </label>
              <input
                type="text"
                value={formData.product_details.currency || 'SAR'}
                onChange={(e) => handleProductDetailChange('currency', e.target.value)}
                className="input w-full"
                placeholder="SAR"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'الفئة' : 'Category'}
              </label>
              <input
                type="text"
                value={formData.product_details.category || ''}
                onChange={(e) => handleProductDetailChange('category', e.target.value)}
                className="input w-full"
                placeholder={isRTL ? 'مثل: إلكترونيات' : 'e.g., Electronics'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'العلامة التجارية' : 'Brand'}
              </label>
              <input
                type="text"
                value={formData.product_details.brand || ''}
                onChange={(e) => handleProductDetailChange('brand', e.target.value)}
                className="input w-full"
                placeholder={isRTL ? 'اسم العلامة التجارية' : 'Brand name'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'SKU' : 'SKU'}
              </label>
              <input
                type="text"
                value={formData.product_details.sku || ''}
                onChange={(e) => handleProductDetailChange('sku', e.target.value)}
                className="input w-full"
                placeholder="PROD-001"
              />
            </div>
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.product_details.in_stock !== false}
                  onChange={(e) => handleProductDetailChange('in_stock', e.target.checked)}
                  className="form-checkbox"
                />
                <span className="text-sm font-medium text-gray-700">
                  {isRTL ? 'متوفر في المخزن' : 'In Stock'}
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Article-specific fields */}
        {isArticle && editing && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'الكاتب' : 'Author'}
              </label>
              <input
                type="text"
                value={formData.article_details.author || ''}
                onChange={(e) => handleArticleDetailChange('author', e.target.value)}
                className="input w-full"
                placeholder={isRTL ? 'اسم الكاتب' : 'Author name'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'الفئة' : 'Category'}
              </label>
              <input
                type="text"
                value={formData.article_details.category || ''}
                onChange={(e) => handleArticleDetailChange('category', e.target.value)}
                className="input w-full"
                placeholder={isRTL ? 'مثل: تكنولوجيا' : 'e.g., Technology'}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'العلامات' : 'Tags'}
              </label>
              <input
                type="text"
                value={formData.article_details.tags ? formData.article_details.tags.join(', ') : ''}
                onChange={(e) => {
                  const tags = e.target.value.split(',').map(t => t.trim()).filter(t => t.length > 0);
                  handleArticleDetailChange('tags', tags);
                }}
                className="input w-full"
                placeholder={isRTL ? 'مفصولة بفواصل' : 'Comma-separated'}
              />
            </div>
          </div>
        )}

        {/* SEO Fields */}
        <div className="space-y-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              {isRTL ? 'إعدادات SEO' : 'SEO Settings'}
            </h3>
            {canEdit && !editing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleGenerateAISEO}
                loading={generatingAISEO}
                disabled={generatingAISEO}
              >
                {isRTL ? '✨ توليد SEO بـ AI' : '✨ Generate SEO with AI'}
              </Button>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? 'Meta Title' : 'Meta Title'}
              <span className="text-xs text-gray-500 ml-1">
                ({formData.meta_title?.length || 0}/60)
              </span>
            </label>
            {editing ? (
              <input
                type="text"
                value={formData.meta_title}
                onChange={(e) => handleChange('meta_title', e.target.value)}
                className="input w-full"
                maxLength={60}
                placeholder={isRTL 
                  ? 'عنوان Meta للـ SEO (50-60 حرف)'
                  : 'Meta title for SEO (50-60 characters)'
                }
              />
            ) : (
              <p className="text-gray-700 p-2 bg-white border border-gray-200 rounded">
                {formData.meta_title || (isRTL ? '(لا يوجد)' : '(None)')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? 'Meta Description' : 'Meta Description'}
              <span className="text-xs text-gray-500 ml-1">
                ({formData.meta_description?.length || 0}/160)
              </span>
            </label>
            {editing ? (
              <textarea
                value={formData.meta_description}
                onChange={(e) => handleChange('meta_description', e.target.value)}
                className="textarea w-full"
                rows="3"
                maxLength={160}
                placeholder={isRTL 
                  ? 'وصف مختصر للمحتوى (150-160 حرف)'
                  : 'Brief description of content (150-160 characters)'
                }
              />
            ) : (
              <p className="text-gray-700 p-2 bg-white border border-gray-200 rounded">
                {formData.meta_description || (isRTL ? '(لا يوجد)' : '(None)')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? 'الكلمات المفتاحية' : 'SEO Keywords'}
            </label>
            {editing ? (
              <input
                type="text"
                value={formData.seo_keywords.join(', ')}
                onChange={handleKeywordsChange}
                className="input w-full"
                placeholder={isRTL 
                  ? 'مفصولة بفواصل (مثل: كلمة1, كلمة2, كلمة3)'
                  : 'Comma-separated (e.g., keyword1, keyword2, keyword3)'
                }
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {formData.seo_keywords.length > 0 ? (
                  formData.seo_keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {keyword}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500 text-sm">
                    {isRTL ? '(لا توجد كلمات مفتاحية)' : '(No keywords)'}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Save/Cancel buttons */}
        {editing && (
          <div className="flex gap-3 pt-4 border-t">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              disabled={loading}
            >
              {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setFormData({
                  rich_description: post.rich_description || '',
                  meta_title: post.meta_title || '',
                  meta_description: post.meta_description || '',
                  seo_keywords: post.seo_keywords || [],
                  product_details: post.product_details || {},
                  article_details: post.article_details || {},
                });
                setEditing(false);
                setError(null);
              }}
            >
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ProductArticleDetailsCard;

